import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCommentDots, FaBell, FaBellSlash, FaTimes, FaPaperPlane } from "react-icons/fa";
import contactApi from "../api/contactApi";

/**
 * LS AI Chat Widget – Improved "Request Demo" workflow
 *
 * Demo flow:
 * 1) Property Type (chips)
 * 2) Property Name
 * 3) Number of Rooms
 * 4) Country (chips)
 * 5) Contact Name
 * 6) Email
 * 7) Phone
 * 8) Notes (optional)
 * 9) Confirm + POST
 *
 * Props:
 * - brandColor       : hex
 * - endpoint         : POST URL for demo submissions
 * - title/subtitle   : header strings
 * - soundOn          : default notification sound on/off
 * - startOpen        : open panel on initial mount
 * - openSignal       : whenever this value changes, widget opens
 * - startQuoteOnOpen : (kept for backwards compatibility) starts demo flow immediately
 * - showBadgeOnLoad/badgeCountOnLoad/notifyOnLoad/notifyDurationMs
 */
export default function AIChatWidget({
  brandColor = "#276F8B",
  title = " Nnetego",
  subtitle = "Ask about features, pricing, or request a demo",
  soundOn = true,
  startOpen = false,
  openSignal = null,
  startQuoteOnOpen = false,
  showBadgeOnLoad = true,
  badgeCountOnLoad = 1,
  notifyOnLoad = true,
  notifyDurationMs = 6000,
}) {
  const [opened, setOpened] = useState(startOpen);
  const [soundEnabled, setSoundEnabled] = useState(soundOn);
  const [history, setHistory] = useState([]); // [{role:'bot'|'user', text}]
  const [flow, setFlow] = useState("idle");   // 'idle' | demo_* steps
  const [sending, setSending] = useState(false);

  // store page-loaded time to support your timing checks server-side
  const loadedAtMsRef = useRef(Date.now());

  const [demo, setDemo] = useState({
    propertyType: "",
    propertyTypeOther: "",
    propertyName: "",
    rooms: "",
    country: "",
    countryOther: "",
    contactName: "",
    email: "",
    phone: "",
    notes: "",
  });

  const [badge, setBadge] = useState(0);
  const [showNudge, setShowNudge] = useState(false);

  const inputRef = useRef(null);
  const bodyRef = useRef(null);
  const audioCtxRef = useRef(null);
  const welcomedRef = useRef(false);
  const skipWelcomeRef = useRef(false);

  // Quick replies
  const [quickRows, setQuickRows] = useState([]);
  const setQuick = (labels = []) => setQuickRows([labels]);
  const setQuick2 = (row1 = [], row2 = []) => setQuickRows([row1, row2].filter(r => r.length));
  const clearQuick = () => setQuickRows([]);

  // Initial badge/nudge on load
  useEffect(() => {
    if (showBadgeOnLoad) setBadge(badgeCountOnLoad);
    if (notifyOnLoad) {
      setShowNudge(true);
      if (notifyDurationMs > 0) {
        const t = setTimeout(() => setShowNudge(false), notifyDurationMs);
        return () => clearTimeout(t);
      }
    }
  }, [showBadgeOnLoad, badgeCountOnLoad, notifyOnLoad, notifyDurationMs]);

  // Minimal audio setup
  function ensureAudio() {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
  }
  function ping() {
    if (!soundEnabled) return;
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const t = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, t);
      o.frequency.exponentialRampToValueAtTime(1320, t + 0.12);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.06, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
      o.connect(g).connect(ctx.destination);
      o.start(); o.stop(t + 0.24);
    } catch {}
  }
  useEffect(() => {
    const unlock = () => ensureAudio();
    window.addEventListener("pointerdown", unlock, { once: true, passive: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const scrollToEnd = () => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const addMsg = (role, text) => {
    setHistory((h) => [...h, { role, text }]);
    setTimeout(scrollToEnd, 0);
    if (role === "bot") ping();
  };
  const addBot = (text) => addMsg("bot", text);

  const welcome = () => {
    addBot("Hi, I’m the your assistant. Ask about features/pricing, or type “demo” to request a demo.");
    setQuick2(["Features", "Pricing"], ["Request Demo"]);
  };

  const resetDemo = () => {
    setDemo({
      propertyType: "",
      propertyTypeOther: "",
      propertyName: "",
      rooms: "",
      country: "",
      countryOther: "",
      contactName: "",
      email: "",
      phone: "",
      notes: "",
    });
  };

  const startDemoFlow = () => {
    resetDemo();
    setFlow("demo_type");
    addBot("Let’s request your demo. What type of property is it?");
    setQuick2(["Hotel", "Guest House", "Resort"], ["B&B", "Other", "Cancel"]);
  };

  const cancelFlow = () => {
    setFlow("idle");
    resetDemo();
    addBot("No problem. What would you like to do?");
    setQuick2(["Features", "Pricing"], ["Request Demo"]);
  };

  // Open/close
  const open = () => {
    setOpened(true);
    setBadge(0);
    setShowNudge(false);

    if (!skipWelcomeRef.current && history.length === 0 && !welcomedRef.current) {
      welcome();
      welcomedRef.current = true;
    }
    setTimeout(() => inputRef.current?.focus(), 60);
  };

  // Programmatic open via prop (Request Demo button)
  useEffect(() => {
    if (openSignal === null) return;

    skipWelcomeRef.current = !!startQuoteOnOpen;
    open();

    if (startQuoteOnOpen) startDemoFlow();

    setTimeout(() => { skipWelcomeRef.current = false; }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  // Intents
  const intents = useMemo(
    () => ({
      features:
        "Highlights: Front Desk, Rooms & Rates, Housekeeping, POS/Restaurant, Mobile Orders, Reports, Multi-property ready.",
      pricing:
        "Pricing depends on rooms, modules, and integrations. Type “demo” to request a demo and receive a tailored quote.",
      phone: "You can call/WhatsApp us at (+267) 71 928 306.",
      email: "Email us at info@lanesands.com.",
      hours: "Business hours are Mon–Fri 08:00–17:00 and Sat 09:00–12:00.",
    }),
    []
  );

  const matchIntent = (t) => {
    const x = String(t || "").toLowerCase();
    if (/\b(demo|request demo|book demo|schedule demo)\b/.test(x)) return "demo";
    if (/\b(feature|features)\b/.test(x)) return "features";
    if (/\bprice|pricing|cost|quote\b/.test(x)) return "pricing";
    if (/\bphone|call|whatsapp|number\b/.test(x)) return "phone";
    if (/\bemail|mail\b/.test(x)) return "email";
    if (/\bhour|open|close|time\b/.test(x)) return "hours";
    if (/\b(cancel|stop|nevermind)\b/.test(x)) return "cancel";
    return "unknown";
  };

  // Validation helpers
  const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
  const validPhone = (s) => String(s || "").replace(/[^\d]/g, "").length >= 7;
  const parseRooms = (s) => {
    const m = String(s || "").match(/\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  };

  const countriesQuick = ["Botswana", "South Africa", "Namibia", "Zimbabwe", "Zambia", "Other", "Cancel"];

  const buildDemoMessage = (d) => {
    const type = d.propertyType === "Other" ? (d.propertyTypeOther || "Other") : d.propertyType;
    const country = d.country === "Other" ? (d.countryOther || "Other") : d.country;

    return [
      "Demo Request (HMS)",
      `Property Type: ${type || "-"}`,
      `Property Name: ${d.propertyName || "-"}`,
      `Rooms: ${d.rooms || "-"}`,
      `Country: ${country || "-"}`,
      "",
      "Contact:",
      `Name: ${d.contactName || "-"}`,
      `Email: ${d.email || "-"}`,
      `Phone: ${d.phone || "-"}`,
      "",
      "Notes:",
      d.notes || "-",
    ].join("\n");
  };

 
  const sendDemo = async () => {
  setSending(true);

  try {
    const payload = {
      propertyType: demo.propertyType,
      propertyTypeOther: demo.propertyTypeOther,
      propertyName: demo.propertyName,
      rooms: Number(parseRooms(demo.rooms) || 0) || null,
      country: demo.country,
      countryOther: demo.countryOther,
      contactName: demo.contactName,
      email: demo.email,
      phone: demo.phone,
      notes: demo.notes,

      // anti-bot fields expected by backend
      company: "",
      ls_Loaded_At: loadedAtMsRef.current,
    };

    await contactApi.requestDemo(payload);

    addBot("Thanks — your demo request is submitted. We’ll contact you shortly.");
    setFlow("idle");
    resetDemo();
    setQuick2(["Features", "Pricing"], ["Request Demo"]);
  } catch (err) {
    // Axios error shape
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Sorry — I couldn’t submit that. Please try again.";

    addBot(msg);
    setQuick2(["Restart", "Request Demo"], ["Email us", "Cancel"]);
  } finally {
    setSending(false);
  }
};


  // Core flow handler
  const handleUser = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return;

    addMsg("user", text);
    clearQuick();

    // global quick commands
    if (/^cancel$/i.test(text)) return cancelFlow();
    if (/^restart$/i.test(text) || /^retry$/i.test(text)) return startDemoFlow();

    // DEMO FLOW
    if (flow === "demo_type") {
      const t = text;
      if (/^other$/i.test(t)) {
        setDemo((d) => ({ ...d, propertyType: "Other" }));
        setFlow("demo_type_other");
        addBot("Please type your property type (e.g., Lodge, Apartments, Camp).");
        setQuick(["Cancel"]);
        return;
      }
      if (!/^(hotel|guest house|resort|b&b)$/i.test(t)) {
        addBot("Please choose one: Hotel, Guest House, Resort, B&B, or Other.");
        setQuick2(["Hotel", "Guest House", "Resort"], ["B&B", "Other", "Cancel"]);
        return;
      }
      setDemo((d) => ({ ...d, propertyType: t }));
      setFlow("demo_name");
      addBot("What is the property name?");
      setQuick(["Cancel"]);
      return;
    }

    if (flow === "demo_type_other") {
      setDemo((d) => ({ ...d, propertyTypeOther: text }));
      setFlow("demo_name");
      addBot("What is the property name?");
      setQuick(["Cancel"]);
      return;
    }

    if (flow === "demo_name") {
      setDemo((d) => ({ ...d, propertyName: text }));
      setFlow("demo_rooms");
      addBot("How many rooms do you have? (a number is fine)");
      setQuick2(["10", "20", "50"], ["100", "Cancel"]);
      return;
    }

    if (flow === "demo_rooms") {
      const n = parseRooms(text);
      if (!n) {
        addBot("Please enter a valid number of rooms (e.g., 24).");
        setQuick2(["10", "20", "50"], ["100", "Cancel"]);
        return;
      }
      setDemo((d) => ({ ...d, rooms: String(n) }));
      setFlow("demo_country");
      addBot("Which country is the property in?");
      setQuick2(countriesQuick.slice(0, 4), countriesQuick.slice(4));
      return;
    }

    if (flow === "demo_country") {
      if (/^other$/i.test(text)) {
        setDemo((d) => ({ ...d, country: "Other" }));
        setFlow("demo_country_other");
        addBot("Please type your country.");
        setQuick(["Cancel"]);
        return;
      }
      setDemo((d) => ({ ...d, country: text }));
      setFlow("demo_contact");
      addBot("Who is the main contact person?");
      setQuick(["Cancel"]);
      return;
    }

    if (flow === "demo_country_other") {
      setDemo((d) => ({ ...d, countryOther: text }));
      setFlow("demo_contact");
      addBot("Who is the main contact person?");
      setQuick(["Cancel"]);
      return;
    }

    if (flow === "demo_contact") {
      setDemo((d) => ({ ...d, contactName: text }));
      setFlow("demo_email");
      addBot("What is their email?");
      setQuick(["Cancel"]);
      return;
    }

    if (flow === "demo_email") {
      if (!validEmail(text)) {
        addBot("That doesn’t look like a valid email. Try again (e.g., name@example.com).");
        setQuick(["Cancel"]);
        return;
      }
      setDemo((d) => ({ ...d, email: text }));
      setFlow("demo_phone");
      addBot("What is their phone/WhatsApp number? (include country code, e.g. +267 …)");
      setQuick(["Cancel"]);
      return;
    }

    if (flow === "demo_phone") {
      if (!validPhone(text)) {
        addBot("That phone looks short — include country code, please (e.g. +267 …).");
        setQuick(["Cancel"]);
        return;
      }
      setDemo((d) => ({ ...d, phone: text }));
      setFlow("demo_notes");
      addBot("Any notes for the demo? (optional) Type ‘skip’ to continue.");
      setQuick2(["Skip"], ["Cancel"]);
      return;
    }

    if (flow === "demo_notes") {
      if (!/^skip$/i.test(text)) setDemo((d) => ({ ...d, notes: text }));

      // confirm
      const summary = buildDemoMessage({
        ...demo,
        notes: /^skip$/i.test(text) ? demo.notes : text,
      });

      setFlow("demo_confirm");
      addBot("Please confirm and I’ll submit this:\n\n" + summary + "\n\nReply: Yes to send, or No to edit.");
      setQuick2(["Yes", "No"], ["Restart", "Cancel"]);
      return;
    }

    if (flow === "demo_confirm") {
      if (/^yes$/i.test(text)) {
        addBot("Submitting your demo request…");
        return void sendDemo();
      }
      if (/^no$/i.test(text)) {
        addBot("No problem — what would you like to change? Type: type / name / rooms / country / contact / email / phone / notes");
        setFlow("demo_edit_pick");
        setQuick2(["Type", "Name", "Rooms"], ["Country", "Contact", "Email"]);
        return;
      }
      addBot("Please reply Yes to send, or No to edit.");
      setQuick2(["Yes", "No"], ["Restart", "Cancel"]);
      return;
    }

    if (flow === "demo_edit_pick") {
      const x = text.toLowerCase();
      const map = {
        type: "demo_type",
        name: "demo_name",
        rooms: "demo_rooms",
        country: "demo_country",
        contact: "demo_contact",
        email: "demo_email",
        phone: "demo_phone",
        notes: "demo_notes",
      };
      const next = map[x];
      if (!next) {
        addBot("Choose one: type / name / rooms / country / contact / email / phone / notes.");
        setQuick2(["Type", "Name", "Rooms"], ["Country", "Contact", "Email"]);
        return;
      }
      setFlow(next);
      // Re-ask prompt for that step
      if (next === "demo_type") {
        addBot("What type of property is it?");
        setQuick2(["Hotel", "Guest House", "Resort"], ["B&B", "Other", "Cancel"]);
        return;
      }
      if (next === "demo_name") { addBot("What is the property name?"); setQuick(["Cancel"]); return; }
      if (next === "demo_rooms") { addBot("How many rooms do you have?"); setQuick2(["10", "20", "50"], ["100", "Cancel"]); return; }
      if (next === "demo_country") { addBot("Which country is the property in?"); setQuick2(countriesQuick.slice(0, 4), countriesQuick.slice(4)); return; }
      if (next === "demo_contact") { addBot("Who is the main contact person?"); setQuick(["Cancel"]); return; }
      if (next === "demo_email") { addBot("What is their email?"); setQuick(["Cancel"]); return; }
      if (next === "demo_phone") { addBot("What is their phone/WhatsApp number?"); setQuick(["Cancel"]); return; }
      if (next === "demo_notes") { addBot("Any notes for the demo? Type ‘skip’ to continue."); setQuick2(["Skip"], ["Cancel"]); return; }
    }

    // If idle, handle intents
    const intent = matchIntent(text);
    switch (intent) {
      case "cancel": cancelFlow(); break;
      case "demo": startDemoFlow(); break;
      case "features": addBot(intents.features); setQuick2(["Pricing"], ["Request Demo"]); break;
      case "pricing": addBot(intents.pricing); setQuick2(["Request Demo"], ["Features"]); break;
      case "phone": addBot(intents.phone); setQuick2(["Request Demo"], ["Email"]); break;
      case "email": addBot(intents.email); setQuick2(["Request Demo"], ["Phone"]); break;
      case "hours": addBot(intents.hours); setQuick2(["Phone", "Email"], ["Request Demo"]); break;
      default:
        addBot("I can help with features, pricing, or demos. Type “demo” to request a demo.");
        setQuick2(["Features", "Pricing"], ["Request Demo"]);
    }
  };

  // Input handlers
  const [draft, setDraft] = useState("");
  const send = () => {
    if (sending) return;
    const t = draft;
    setDraft("");
    handleUser(t);
  };
  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  useEffect(() => { if (badge > 0) ping(); /* eslint-disable-next-line */ }, [badge]);

  return (
    <>
      {/* Red nudge on load (auto-hide) */}
      <div
        className={`lsai-nudge ${showNudge ? "show" : ""}`}
        role="status"
        aria-live="polite"
        onClick={() => { open(); setShowNudge(false); }}
        title="Open AI chat"
      >
        <span className="dot" /> New message
      </div>

      {/* Floating button */}
      <button
        className="lsai-btn"
        aria-label="Open chat"
        onClick={() => (opened ? setOpened(false) : open())}
        title={opened ? "Close chat" : "Open chat"}
      >
        <FaCommentDots size={22} />
        {badge > 0 && <span className="lsai-badge">{badge}</span>}
      </button>

      {/* Panel */}
      <div className={`lsai-panel ${opened ? "open" : ""}`} role="dialog" aria-label="LS chat">
        <div className="lsai-header" style={{ background: `linear-gradient(90deg, ${brandColor}, #46a0ff)` }}>
         <div className="lsai-logo" aria-hidden="true">
  <img
    src="/images/logo.png"
    alt="Nnetego"
    className="lsai-logo-img"
    loading="eager"
    decoding="async"
    onError={(e) => {
      // fallback to initials if image missing
      e.currentTarget.style.display = "none";
      const parent = e.currentTarget.parentElement;
      if (parent && !parent.querySelector(".lsai-logo-fallback")) {
        const span = document.createElement("span");
        span.className = "lsai-logo-fallback";
        span.textContent = "N";
        parent.appendChild(span);
      }
    }}
  />
</div>

          <div className="lsai-headcopy">
            <div className="lsai-title">{title}</div>
            <div className="lsai-sub">{subtitle}</div>
          </div>
          <div className="lsai-tools">
            <button
              className="lsai-tool"
              aria-pressed={soundEnabled}
              title={soundEnabled ? "Sound on" : "Sound off"}
              onClick={() => setSoundEnabled((v) => !v)}
            >
              {soundEnabled ? <FaBell /> : <FaBellSlash />}
            </button>
            <button className="lsai-tool" title="Close" onClick={() => setOpened(false)}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="lsai-body" ref={bodyRef}>
          {history.map((m, i) => (
            <div key={i} className={`lsai-msg ${m.role === "bot" ? "bot" : "user"}`}>
              <div className="lsai-bubble">{m.text}</div>
            </div>
          ))}

          {quickRows.length > 0 && (
            <div className="lsai-quick">
              {quickRows.map((row, idx) => (
                <div className="lsai-quick-row" key={idx}>
                  {row.map((label) => (
                    <button
                      key={label}
                      className="lsai-chip"
                      onClick={() => handleUser(label)}
                      disabled={sending}
                      aria-disabled={sending}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lsai-input">
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            placeholder={sending ? "Submitting..." : "Type a message…"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            aria-label="Message"
            disabled={sending}
          />
          <button className="lsai-send" onClick={send} title="Send" disabled={sending}>
            <FaPaperPlane />
          </button>
        </div>
      </div>

      {/* Styles (scoped) */}
      <style>{`
        :root{
          --lsai-bg:#fff; --lsai-border:#e6eef5; --lsai-muted:#64748b; --lsai-text:#0f172a;
          --lsai-shadow:0 22px 60px rgba(12,22,44,.18); --lsai-red:#ff375f;
        }
        .lsai-nudge{ position:fixed; right:88px; bottom:96px; z-index:2147483001; background:var(--lsai-red); color:#fff; border-radius:999px; padding:8px 12px; font-size:13px; font-weight:600; display:flex; align-items:center; gap:8px; box-shadow:0 14px 30px rgba(255,55,95,.3); transform:translateY(10px); opacity:0; pointer-events:none; transition:transform .2s ease, opacity .2s ease; }
        .lsai-nudge .dot{ width:10px; height:10px; border-radius:50%; background:#fff; box-shadow:0 0 0 2px rgba(255,255,255,.35); animation: lsai-pulse 1.4s ease-out infinite; }
        .lsai-nudge.show{ transform:translateY(0); opacity:1; pointer-events:auto; }
        @keyframes lsai-pulse { 0% { box-shadow:0 0 0 0 rgba(255,255,255,.6) } 70%{ box-shadow:0 0 0 10px rgba(255,255,255,0) } 100%{ box-shadow:0 0 0 0 rgba(255,255,255,0) } }

        .lsai-btn{ position:fixed; right:20px; bottom:20px; z-index:2147483000; width:56px; height:56px; border-radius:50%; border:none; cursor:pointer; background:${brandColor}; color:#fff; display:grid; place-items:center; box-shadow:0 12px 30px rgba(0,0,0,.2); transition:transform .15s ease; }
        .lsai-btn:hover{ transform:translateY(-1px); }
        .lsai-badge{ position:absolute; top:-4px; right:-4px; min-width:18px; height:18px; padding:0 6px; display:grid; place-items:center; background:var(--lsai-red); color:#fff; font-size:11px; border-radius:999px; box-shadow:0 0 0 2px #fff; }

        .lsai-panel{ position:fixed; right:20px; bottom:86px; width:360px; max-width:95vw; background:var(--lsai-bg); border:1px solid rgba(12,22,44,.08); border-radius:14px; overflow:hidden; box-shadow:var(--lsai-shadow); display:none; flex-direction:column; z-index:2147483000; }
        .lsai-panel.open{ display:flex; }

        .lsai-header{ display:flex; align-items:center; gap:10px; padding:12px 14px; color:#fff; }
        .lsai-logo{ width:28px; height:28px; border-radius:6px; color:${brandColor}; display:grid; place-items:center; font-weight:800; }
        .lsai-title{ font-weight:700; line-height:1.1 }
        .lsai-sub{ font-size:12px; opacity:.95; margin-top:1px }
        .lsai-tools{ margin-left:auto; display:flex; gap:6px }
        .lsai-tool{ background:transparent; border:none; color:#fff; font-size:16px; cursor:pointer; opacity:.95 }

        .lsai-body{ height:320px; overflow:auto; padding:12px; background:#fafbfc; }
        .lsai-msg{ display:flex; margin:8px 0; }
        .lsai-msg.bot{ justify-content:flex-start }
        .lsai-msg.user{ justify-content:flex-end }
        .lsai-bubble{ max-width:78%; padding:10px 12px; border-radius:12px; font-size:14px; line-height:1.35; box-shadow:0 1px 0 rgba(0,0,0,.03) }
        .lsai-msg.bot .lsai-bubble{ background:#fff; color:var(--lsai-text); border:1px solid rgba(12,22,44,.08) }
        .lsai-msg.user .lsai-bubble{ background:${brandColor}; color:#fff }

        .lsai-quick{ margin-top:6px; display:flex; flex-direction:column; gap:6px }
        .lsai-quick-row{ display:flex; gap:8px; flex-wrap:wrap }
        .lsai-chip{ font-size:12px; background:#eef4ff; color:#1244d8; border:1px solid #d9e6ff; padding:6px 10px; border-radius:999px; cursor:pointer; }
        .lsai-chip[disabled]{ opacity:.6; cursor:not-allowed; }

        .lsai-input{ display:flex; gap:8px; padding:10px; border-top:1px solid #eef1f5; background:#fff; }
        .lsai-input textarea{ flex:1; border:1px solid #dde3ea; border-radius:10px; padding:10px 12px; font-size:14px; outline:none; resize:none; max-height:110px; min-height:40px; line-height:1.35; }
        .lsai-input textarea:disabled{ opacity:.7; background:#f7f7f7; }
        .lsai-send{ background:${brandColor}; color:#fff; border:none; border-radius:10px; width:44px; display:grid; place-items:center; cursor:pointer; }
        .lsai-send:disabled{ opacity:.6; cursor:not-allowed; }
      `}</style>
    </>
  );
}
